import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import type { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import type { JSX } from 'react'
import { z } from 'zod'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useValidatedToolForm } from '#src/components/validated-tool-form/shared/useValidatedToolForm.ts'
import {
  ValidatedToolInnerForm,
  type ValidatedToolInnerFormProps,
} from '#src/components/validated-tool-form/shared/ValidatedToolInnerForm.tsx'
import { ValidatedToolSection } from '#src/components/validated-tool-form/shared/ValidatedToolSection.tsx'

export interface SLUMSFormProps extends Omit<
  ValidatedToolInnerFormProps,
  'validatedTool' | 'form' | 'subtitle'
> {
  response?: ValidatedToolResponse
  restorationId?: string
}

const schema = z.object({
  '/71492-3': z.number().int().nullable(),
  summary: z.string().nullable(),
})

const validatedTool = new ValidatedTool(questionnaires.slumsQuestionnaire)

export function SLUMSForm({
  response,
  restorationId,
  ...delegated
}: SLUMSFormProps): JSX.Element {
  const form = useValidatedToolForm({
    schema,
    validatedTool,
    response,
    restorationId,
  })

  return (
    <ValidatedToolInnerForm
      validatedTool={validatedTool}
      form={form}
      response={response}
      {...delegated}
    >
      <ValidatedToolSection title="Total score">
        <FormField
          control={form.control}
          name="/71492-3"
          render={({ field: { value, onChange, ...rest } }) => (
            <Input
              value={value ?? 0}
              onChange={(e) => onChange(e.target.valueAsNumber)}
              type="number"
              className="mt-1 w-auto place-self-start"
              {...rest}
            />
          )}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="Summary">
        <FormField
          control={form.control}
          name="summary"
          render={({ field: { value, ...rest } }) => (
            <RichTextEditor
              defaultValue={value ?? ''}
              placeholder="Add summary…"
              className="mt-1"
              contentClassName="max-w-(--container-prose)"
              {...rest}
            />
          )}
        />
      </ValidatedToolSection>
      {!response && (
        <div>
          <FormSubmitButton control={form.control}>Submit</FormSubmitButton>
        </div>
      )}
    </ValidatedToolInnerForm>
  )
}
